import { useTranslation } from '../i18n/useTranslation';
import type { RiskMetrics } from '../types/gateway';

interface RiskPanelProps {
  risk: RiskMetrics;
  className?: string;
}

type ColorClass = 'text-danger' | 'text-warning' | 'text-success';
type BgColorClass = 'bg-danger' | 'bg-warning' | 'bg-success';

function evtVarColor(pct: number): ColorClass {
  if (pct < -0.05) return 'text-danger';
  if (pct < -0.03) return 'text-warning';
  return 'text-success';
}

function evtVarBg(pct: number): BgColorClass {
  if (pct < -0.05) return 'bg-danger';
  if (pct < -0.03) return 'bg-warning';
  return 'bg-success';
}

function liquidationColor(pct: number): ColorClass {
  if (pct > 0.01) return 'text-danger';
  if (pct > 0.005) return 'text-warning';
  return 'text-success';
}

function liquidationBg(pct: number): BgColorClass {
  if (pct > 0.01) return 'bg-danger';
  if (pct > 0.005) return 'bg-warning';
  return 'bg-success';
}

function garchColor(pct: number): ColorClass {
  if (pct > 0.03) return 'text-danger';
  if (pct > 0.02) return 'text-warning';
  return 'text-success';
}

function garchBg(pct: number): BgColorClass {
  if (pct > 0.03) return 'bg-danger';
  if (pct > 0.02) return 'bg-warning';
  return 'bg-success';
}

function skewColor(skew: string): ColorClass {
  if (skew.startsWith('-')) return 'text-danger';
  return 'text-success';
}

function formatPct(value: number, decimals = 1): string {
  return (value * 100).toFixed(decimals) + '%';
}

/** Width of the progress bar: absolute value, capped at 100% of a reference max */
function barWidthPct(value: number, max: number): number {
  return Math.min((Math.abs(value) / max) * 100, 100);
}

interface MetricRowProps {
  label: string;
  value: string;
  colorClass: ColorClass;
  bgColorClass: BgColorClass;
  /** bar width 0-100 */
  barWidth: number;
  showBar: boolean;
}

function MetricRow({ label, value, colorClass, bgColorClass, barWidth, showBar }: MetricRowProps) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 border-b border-border-subtle last:border-b-0">
      <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest w-[90px] shrink-0">
        {label}
      </span>

      {showBar && (
        <div
          className="flex-1 h-1 bg-border rounded-full overflow-hidden"
          role="presentation"
        >
          <div
            className={`h-full rounded-full ${bgColorClass}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
      )}

      {!showBar && <div className="flex-1" />}

      <span className={`text-[10px] font-mono tabular-nums ${colorClass} w-[52px] text-right shrink-0`}>
        {value}
      </span>
    </div>
  );
}

export function RiskPanel({ risk, className = '' }: RiskPanelProps) {
  const { t } = useTranslation();
  const evtColor = evtVarColor(risk.evt_es_99);
  const evtBg = evtVarBg(risk.evt_es_99);
  const varColor = evtVarColor(risk.var_95);
  const varBg = evtVarBg(risk.var_95);
  const liqColor = liquidationColor(risk.liquidation_cascade_prob);
  const liqBg = liquidationBg(risk.liquidation_cascade_prob);
  const garchColorCls = garchColor(risk.garch_vol);
  const garchBgCls = garchBg(risk.garch_vol);
  const skewColorCls = skewColor(risk.implied_skew);

  return (
    <div
      className={`bg-card rounded border border-border flex flex-col ${className}`}
      role="region"
      aria-label="Risk panel"
    >
      {/* Panel header */}
      <div className="h-6 flex items-center px-2 border-b border-border-subtle shrink-0">
        <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest">
          {t('risk.header')}
        </span>
      </div>

      {/* Metric rows */}
      <div className="flex-1 flex flex-col">
        <MetricRow
          label="EVT ES99"
          value={formatPct(risk.evt_es_99)}
          colorClass={evtColor}
          bgColorClass={evtBg}
          barWidth={barWidthPct(risk.evt_es_99, 0.1)}
          showBar
        />
        <MetricRow
          label="VaR 95"
          value={formatPct(risk.var_95)}
          colorClass={varColor}
          bgColorClass={varBg}
          barWidth={barWidthPct(risk.var_95, 0.1)}
          showBar
        />
        <MetricRow
          label="Liquidation"
          value={formatPct(risk.liquidation_cascade_prob, 1)}
          colorClass={liqColor}
          bgColorClass={liqBg}
          barWidth={barWidthPct(risk.liquidation_cascade_prob, 0.02)}
          showBar
        />
        <MetricRow
          label="GARCH Vol"
          value={formatPct(risk.garch_vol, 2)}
          colorClass={garchColorCls}
          bgColorClass={garchBgCls}
          barWidth={barWidthPct(risk.garch_vol, 0.05)}
          showBar
        />
        <MetricRow
          label="Implied Skew"
          value={risk.implied_skew}
          colorClass={skewColorCls}
          bgColorClass="bg-success"
          barWidth={0}
          showBar={false}
        />
      </div>
    </div>
  );
}
